import React from 'react';
import Input from '../../../components/ui/Input';

// Compute concrete from/to ISO date strings for a preset key.
// Uses toISOString() to match the existing useState initializer pattern in
// report-builder/index.jsx so the default and the Last 30 days preset stay
// consistent in their UTC-rounded boundaries.
const toIso = (d) => d?.toISOString()?.split('T')?.[0];

const computePreset = (key) => {
  const today = new Date();
  switch (key) {
    case 'last7': {
      const from = new Date(today);
      from?.setDate(from?.getDate() - 6);
      return { from: toIso(from), to: toIso(today) };
    }
    case 'last30': {
      const from = new Date(today);
      from?.setDate(from?.getDate() - 29);
      return { from: toIso(from), to: toIso(today) };
    }
    case 'last90': {
      const from = new Date(today);
      from?.setDate(from?.getDate() - 89);
      return { from: toIso(from), to: toIso(today) };
    }
    case 'thisMonth': {
      const from = new Date(today?.getFullYear(), today?.getMonth(), 1);
      return { from: toIso(from), to: toIso(today) };
    }
    case 'lastMonth': {
      const from = new Date(today?.getFullYear(), today?.getMonth() - 1, 1);
      // Day 0 of the next month = last day of the previous month.
      const to = new Date(today?.getFullYear(), today?.getMonth(), 0);
      return { from: toIso(from), to: toIso(to) };
    }
    default:
      return null;
  }
};

const PRESETS = [
  { key: 'last7', label: 'Last 7 days' },
  { key: 'last30', label: 'Last 30 days' },
  { key: 'thisMonth', label: 'This month' },
  { key: 'lastMonth', label: 'Last month' },
  { key: 'last90', label: 'Last 90 days' },
];

const DateRangeBar = ({ dateFrom, dateTo, onDateFromChange, onDateToChange, error }) => {
  const today = toIso(new Date());

  // First preset whose computed range matches the current selection wins.
  // No match → null → the Custom indicator pill highlights instead.
  const activePresetKey = (() => {
    for (const p of PRESETS) {
      const range = computePreset(p?.key);
      if (range && range?.from === dateFrom && range?.to === dateTo) return p?.key;
    }
    return null;
  })();

  const handlePresetClick = (key) => {
    const range = computePreset(key);
    if (!range) return;
    onDateFromChange?.(range?.from);
    onDateToChange?.(range?.to);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-1.5">Date Range</label>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">From</label>
          <Input
            type="date"
            value={dateFrom || ''}
            max={today}
            onChange={(e) => onDateFromChange?.(e?.target?.value)}
            className="text-sm"
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">To</label>
          <Input
            type="date"
            value={dateTo || ''}
            max={today}
            onChange={(e) => onDateToChange?.(e?.target?.value)}
            className="text-sm"
          />
        </div>
      </div>

      {error && (
        <p className="text-xs text-red-600 mb-2" role="alert">{error}</p>
      )}

      <div className="flex flex-wrap gap-1.5">
        {PRESETS?.map(p => {
          const isActive = activePresetKey === p?.key;
          return (
            <button
              key={p?.key}
              type="button"
              onClick={() => handlePresetClick(p?.key)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
                isActive
                  ? 'border-accent bg-accent/10 text-accent font-medium'
                  : 'border-border text-muted-foreground hover:border-accent/50'
              }`}
            >
              {p?.label}
            </button>
          );
        })}
        {/* Custom indicator — visual only, non-interactive. Highlights when
            the current range matches no preset. */}
        <span
          className={`text-xs px-2.5 py-1 rounded-full border ${
            activePresetKey === null
              ? 'border-accent bg-accent/10 text-accent font-medium'
              : 'border-border text-muted-foreground'
          }`}
          aria-label="Custom range indicator"
        >
          Custom
        </span>
      </div>
    </div>
  );
};

export default DateRangeBar;
